import { useWatch } from 'react-hook-form';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import GavelIcon from '@mui/icons-material/Gavel';
import SectionHeader from '../../../components/shared/SectionHeader';
import CheckboxField from '../../../components/form/CheckboxField';
import { fmtDollar, fmtPhone } from '../../../utils/formatters';

const SummaryCard = ({ icon, title, items }) => {
  const visible = items.filter((i) => i.value);
  if (visible.length === 0) return null;

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
          <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
          <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
        </Stack>
        <Stack spacing={0.75}>
          {visible.map((item) => (
            <Stack key={item.label} direction="row" justifyContent="space-between" spacing={2}>
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
              <Typography variant="caption" fontWeight={600} textAlign="right">{item.value}</Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

const ReviewDeclarationsStep = () => {
  const form = useWatch();
  const pi = form?.personalInfo || {};
  const emp = form?.employment || {};
  const prop = form?.property || {};
  const ld = form?.loanDetails || {};
  const isRefi = ld.loanPurpose === 'Refinance' || ld.loanPurpose === 'Cash-Out Refinance';

  return (
    <Box>
      <SectionHeader title="Review & Declarations" icon={<AssignmentTurnedInIcon />} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review your application and answer the declaration questions
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SummaryCard
            icon={<PersonIcon fontSize="small" />}
            title="Personal Information"
            items={[
              { label: 'Name', value: [pi.firstName, pi.lastName].filter(Boolean).join(' ') },
              { label: 'Email', value: pi.email || '' },
              { label: 'Phone', value: pi.phone ? fmtPhone(pi.phone) : '' },
              {
                label: 'Address',
                value: [pi.currentAddress, pi.currentCity, pi.currentState, pi.currentZip].filter(Boolean).join(', '),
              },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SummaryCard
            icon={<WorkIcon fontSize="small" />}
            title="Employment"
            items={[
              { label: 'Employer', value: emp.employerName || '' },
              { label: 'Position', value: emp.position || '' },
              {
                label: 'Monthly Income',
                value: emp.totalMonthlyIncome != null ? fmtDollar(emp.totalMonthlyIncome) : '',
              },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SummaryCard
            icon={<HomeWorkIcon fontSize="small" />}
            title="Property"
            items={[
              {
                label: 'Address',
                value: [prop.address, prop.city, prop.state, prop.zip].filter(Boolean).join(', '),
              },
              { label: 'Value', value: prop.propertyValue != null ? fmtDollar(prop.propertyValue) : '' },
              { label: 'Type', value: prop.propertyType || '' },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SummaryCard
            icon={<RequestQuoteIcon fontSize="small" />}
            title="Loan Details"
            items={[
              { label: 'Purpose', value: ld.loanPurpose || '' },
              {
                label: isRefi ? 'New Loan Amount' : 'Loan Amount',
                value: ld.loanAmount != null ? fmtDollar(ld.loanAmount) : '',
              },
              { label: 'Type', value: ld.loanType || '' },
              ...(!isRefi
                ? [{ label: 'Down Payment', value: ld.downPayment != null ? fmtDollar(ld.downPayment) : '' }]
                : [
                    {
                      label: 'Current Balance',
                      value: ld.currentMortgageBalance != null ? fmtDollar(ld.currentMortgageBalance) : '',
                    },
                    {
                      label: 'Current Rate',
                      value: ld.currentInterestRate != null
                        ? `${Number(ld.currentInterestRate).toFixed(3)}%`
                        : '',
                    },
                  ]),
              ...(ld.loanPurpose === 'Cash-Out Refinance' && ld.cashOutAmount
                ? [{ label: 'Cash-Out', value: fmtDollar(ld.cashOutAmount) }]
                : []),
            ]}
          />
        </Grid>
      </Grid>

      <SectionHeader title="Declarations" icon={<GavelIcon />} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Please answer the following questions honestly. These are required by federal law.
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={0.5}>
            <CheckboxField name="declarations.outstandingJudgments" label="Are there any outstanding judgments against you?" />
            <CheckboxField name="declarations.bankruptcyLast7Years" label="Have you declared bankruptcy within the past 7 years?" />
            <CheckboxField name="declarations.foreclosureLast7Years" label="Have you had property foreclosed upon in the last 7 years?" />
            <CheckboxField name="declarations.lawsuitParty" label="Are you a party to a lawsuit?" />
            <CheckboxField name="declarations.delinquentFederalDebt" label="Are you presently delinquent on any federal debt?" />
            <CheckboxField name="declarations.alimonyObligation" label="Are you obligated to pay alimony, child support, or separate maintenance?" />
            {!isRefi && (
              <CheckboxField name="declarations.downPaymentBorrowed" label="Is any part of the down payment borrowed?" />
            )}
          </Stack>
        </CardContent>
      </Card>

      <SectionHeader title="Citizenship & Military" />
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={0.5}>
            <CheckboxField name="declarations.usCitizen" label="Are you a U.S. citizen?" />
            <CheckboxField name="declarations.currentlyServing" label="Are you currently serving on active military duty?" />
            <CheckboxField name="declarations.previouslyServed" label="Have you previously served in the U.S. Armed Forces?" />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReviewDeclarationsStep;
